import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import Head from 'next/head';
import clsx from 'clsx';
import { Disclosure } from '@headlessui/react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  MapIcon,
  Squares2X2Icon,
  Bars3Icon,
  AdjustmentsHorizontalIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';

// Components
import Container from '@/components/Container';
import ParkItemsGrid from '@/components/ParkItemsGrid';
import Pagination from '@/components/Pagination';
import Checkbox from '@/components/Checkbox';
import TypeaheadInput from '@/components/TypeaheadInput';
import PillBadge from '@/components/PillBadge';
import Map, { ParkMarker, StateBoundary } from '@/components/Map';

// Hooks
import { useFilters } from '@/hooks/useFilters';
import { useURLSync } from '@/hooks/useURLSync';

// Data
import { getParksForListing } from '@/lib/db-queries';
import prisma from '@/lib/database';
import { getStates, slugify } from '@/lib/util';

const ITEMS_PER_PAGE = 12;

// US States list for filter
const US_STATES = getStates();

// Amenity categories (same as park detail page)
const AMENITY_CATEGORIES = {
  'Information & Services': {
    icon: 'circle-info',
    items: [
      'information',
      'information - maps available',
      'information - ranger/staff member present',
      'information kiosk/bulletin board',
      'information - park newspaper available',
      'entrance passes for sale',
      'ticket sales',
      'permits issued',
      'junior ranger booklet available',
    ],
  },
  'Restrooms & Facilities': {
    icon: 'restroom',
    items: [
      'restroom',
      'restroom - family',
      'restroom - accessible',
      'toilet - flush',
      'toilet - vault/pit',
      'showers',
      'baby changing station',
      'trash/litter receptacles',
    ],
  },
  'Parking & Transportation': {
    icon: 'square-parking',
    items: [
      'parking - auto',
      'parking - bus/rv',
      'bus/shuttle stop',
      'fuel (unleaded)',
      'sewage dump station - boat/rv',
      'electric vehicle charging',
    ],
  },
  Accessibility: {
    icon: 'universal-access',
    items: [
      'wheelchair accessible',
      'accessible rooms',
      'accessible sites',
      'assistive listening systems',
      'captioned media',
      'braille',
      'sign language',
      'automated entrance',
      'elevator',
    ],
  },
  'Food & Shopping': {
    icon: 'utensils',
    items: [
      'food/drink - restaurant/table service',
      'food/drink - snacks',
      'food/drink - cafeteria',
      'food/drink - ice cream',
      'food/drink - vending',
      'gifts/souvenirs/books',
      'atm/cash machine',
    ],
  },
  'Water & Utilities': {
    icon: 'droplet',
    items: [
      'water - drinking/potable',
      'water - bottle-filling station',
      'internet/wifi available',
      'cellular signal',
      'telephone',
      'electricity hookup',
      'water hookup',
    ],
  },
  'Recreation & Education': {
    icon: 'camera',
    items: [
      'theater/auditorium',
      'historical/interpretive information/exhibits',
      'scenic view/photo spot',
      'picnic table',
      'benches/seating',
      'grill',
      'fire pit',
      'playground',
      'bicycle - rental',
    ],
  },
  'Medical & Safety': {
    icon: 'kit-medical',
    items: [
      'first aid kit available',
      'first aid/medical care available',
      'automated external defibrillator (aed)',
    ],
  },
  'Pet & Animal Services': {
    icon: 'paw',
    items: [
      'pets allowed',
      'animal-safe food storage',
      'hitching post/corral',
      'fish-cleaning station',
    ],
  },
  'Water Access': {
    icon: 'anchor',
    items: [
      'beach/water access',
      'dock/pier',
      'canoe/kayak/small boat launch',
      'boat launch',
      'marina',
    ],
  },
};

// Activity categories (same as Activities component)
const ACTIVITY_CATEGORIES = {
  'Outdoor Recreation': {
    icon: 'person-hiking',
    items: [
      'hiking',
      'biking',
      'road biking',
      'mountain biking',
      'camping',
      'backcountry camping',
      'car or front country camping',
      'rv camping',
      'rock climbing',
      'canyoneering',
      'scenic driving',
      'auto and atv',
      'off-road driving',
      'horseback riding',
      'caving',
      'geocaching',
      'orienteering',
      'picnicking',
      'playground',
      'off-roading',
      'mountain climbing',
      'horse trekking',
      'compass',
      'gps',
    ],
  },
  'Water Activities': {
    icon: 'water',
    items: [
      'swimming',
      'fishing',
      'fly fishing',
      'freshwater fishing',
      'saltwater fishing',
      'kayaking',
      'canoeing',
      'paddling',
      'boating',
      'sailing',
      'surfing',
      'scuba diving',
      'snorkeling',
      'tubing',
      'water skiing',
      'whitewater rafting',
      'stand up paddleboarding',
      'jet skiing',
      'boat tour',
    ],
  },
  'Winter Activities': {
    icon: 'snowflake',
    items: [
      'skiing',
      'cross-country skiing',
      'downhill skiing',
      'snowboarding',
      'snowshoeing',
      'snowmobiling',
      'ice skating',
      'ice climbing',
      'sledding',
      'snow play',
    ],
  },
  'Wildlife & Nature': {
    icon: 'binoculars',
    items: [
      'wildlife watching',
      'birdwatching',
      'bird watching',
      'park film',
      'stargazing',
      'astronomy',
      'wildflower viewing',
      'nature walks',
      'botanical garden',
      'tree viewing',
      'planetarium',
      'gathering and foraging',
    ],
  },
  'Cultural & Educational': {
    icon: 'landmark',
    items: [
      'guided tours',
      'self-guided tours - walking',
      'museum exhibits',
      'living history',
      'bookstore and/or park store',
      'arts and culture',
      'hands-on',
      'craft demonstrations',
      'cultural demonstrations',
      'first person interpretation',
      'ranger led programs',
      'junior ranger program',
      'citizen science',
      'research',
      'archeology',
      'reenactments',
      'live music',
      'theater',
      'historic weapons',
      'arts and crafts',
      'bus/shuttle guided tour',
      'bookstore and park store',
    ],
  },
  'Sports & Recreation': {
    icon: 'volleyball',
    items: [
      'golfing',
      'tennis',
      'team sports',
      'disc golf',
      'hunting',
      'archery',
      'shooting',
      'sports',
      'mini-golf',
    ],
  },
};

export default function FindAPark({
  parks,
  designations,
  activities,
  amenities,
  totalParks,
}) {
  const [showMap, setShowMap] = useState(true);
  const [selectedParkId, setSelectedParkId] = useState(null);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [isListView, setIsListView] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearchRef = useRef(null);

  // Search state for filter inputs
  const [stateSearchTerm, setStateSearchTerm] = useState('');
  const [designationSearchTerm, setDesignationSearchTerm] = useState('');
  const [activitySearchTerm, setActivitySearchTerm] = useState('');
  const [amenitySearchTerm, setAmenitySearchTerm] = useState('');

  // Disclosure open state - keep panels open when they have active filters
  const [stateOpen, setStateOpen] = useState(true);
  const [designationOpen, setDesignationOpen] = useState(true);
  const [activityOpen, setActivityOpen] = useState(false);
  const [amenityOpen, setAmenityOpen] = useState(false);

  // Filter state management
  const {
    filters,
    setFilters,
    addFilter,
    removeFilter,
    toggleFilter,
    clearFilters,
    hasFilter,
    activeFilterCount,
    activeFiltersArray,
  } = useFilters({
    state: [],
    designation: [],
    activity: [],
    amenity: [],
    search: [],
  });

  // URL sync for shareable links
  const { isReady } = useURLSync({
    filters,
    setFilters,
    paramMapping: {
      state: 'state',
      designation: 'type',
      activity: 'activity',
      amenity: 'amenity',
      search: 'q',
    },
  });

  // Filter parks based on active filters
  const filteredParks = useMemo(() => {
    if (!parks) return [];
    return parks.filter((park) => {
      // State filter
      const stateFilters = filters?.state || [];
      if (stateFilters.length > 0) {
        const parkStates = park.states?.split(',').map((s) => s.trim()) || [];
        const hasMatchingState = stateFilters.some((state) =>
          parkStates.includes(state)
        );
        if (!hasMatchingState) return false;
      }

      // Designation filter
      const designationFilters = filters?.designation || [];
      if (designationFilters.length > 0) {
        if (!designationFilters.includes(park.designation)) return false;
      }

      // Activity filter (using slugified activity names) - AND logic: park must have ALL selected activities
      const activityFilters = filters?.activity || [];
      if (activityFilters.length > 0) {
        const parkActivitySlugs =
          park.activities?.map((name) => slugify(name)).filter(Boolean) || [];
        const hasAllActivities = activityFilters.every((actSlug) =>
          parkActivitySlugs.includes(actSlug)
        );
        if (!hasAllActivities) return false;
      }

      // Amenity filter (using slugified amenity names) - AND logic: park must have ALL selected amenities
      const amenityFilters = filters?.amenity || [];
      if (amenityFilters.length > 0) {
        const parkAmenitySlugs =
          park.amenities?.map((name) => slugify(name)).filter(Boolean) || [];
        const hasAllAmenities = amenityFilters.every((amenSlug) =>
          parkAmenitySlugs.includes(amenSlug)
        );
        if (!hasAllAmenities) return false;
      }

      // Search filter
      const searchFilters = filters?.search || [];
      if (searchFilters.length > 0) {
        const searchTerm = searchFilters[0]?.toLowerCase();
        if (searchTerm) {
          const searchableText = [
            park.fullName,
            park.name,
            park.description,
            park.states,
          ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();
          if (!searchableText.includes(searchTerm)) return false;
        }
      }

      return true;
    });
  }, [parks, filters]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = Math.ceil(filteredParks.length / ITEMS_PER_PAGE);

  // Get current page of parks
  const paginatedParks = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredParks.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredParks, currentPage]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, setCurrentPage]);

  // Parks with coordinates for map
  // When state filter is active, exclude multi-state parks (3+ states) from map
  // since their single coordinate may be in a different state along the route
  const parksWithCoords = useMemo(() => {
    const hasStateFilter = filters?.state?.length > 0;
    return filteredParks.filter((p) => {
      if (!p.latitude || !p.longitude) return false;

      // If filtering by state, hide parks that span 3+ states (usually trails)
      // Their coordinates are often in a different state than the one filtered
      if (hasStateFilter) {
        const stateCount = p.states?.split(',').length || 1;
        if (stateCount >= 3) return false;
      }

      return true;
    });
  }, [filteredParks, filters?.state]);

  // Group parks by location (rounded to 3 decimal places) for clustered markers
  const parksByLocation = useMemo(() => {
    const locationMap = {};

    parksWithCoords.forEach((park) => {
      const lat = parseFloat(park.latitude);
      const lng = parseFloat(park.longitude);

      // Create a location key (rounded to 3 decimal places for grouping nearby parks)
      const locationKey = `${lat.toFixed(3)},${lng.toFixed(3)}`;

      if (!locationMap[locationKey]) {
        locationMap[locationKey] = {
          lat,
          lng,
          parks: [],
          locationKey,
        };
      }
      locationMap[locationKey].parks.push(park);
    });

    return Object.values(locationMap);
  }, [parksWithCoords]);

  // Map bounds for auto-fit
  const mapBounds = useMemo(() => {
    if (parksByLocation.length === 0) return null;
    return parksByLocation.map(({ lat, lng }) => [lat, lng]);
  }, [parksByLocation]);

  // Handle park selection from map
  const handleParkSelect = useCallback((park) => {
    setSelectedParkId(park.id);
    // Scroll to park in list if needed
    const element = document.getElementById(`park-${park.id}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, []);

  // Handle search input with debouncing
  const handleSearch = useCallback(
    (e) => {
      const value = e.target.value;
      setSearchInput(value); // Update input immediately for responsive UX

      // Clear any pending debounced search
      if (debouncedSearchRef.current) {
        clearTimeout(debouncedSearchRef.current);
      }

      // Debounce the actual filter update
      debouncedSearchRef.current = setTimeout(() => {
        if (value) {
          setFilters((prev) => ({ ...prev, search: [value] }));
        } else {
          setFilters((prev) => ({ ...prev, search: [] }));
        }
      }, 300);
    },
    [setFilters]
  );

  // Sync searchInput with filters when URL params are restored
  useEffect(() => {
    const searchValue = filters.search[0] || '';
    if (searchValue !== searchInput) {
      setSearchInput(searchValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search]);

  // Keep disclosure panels open when they have active filters
  useEffect(() => {
    if (filters.state?.length > 0) setStateOpen(true);
    if (filters.designation?.length > 0) setDesignationOpen(true);
    if (filters.activity?.length > 0) setActivityOpen(true);
    if (filters.amenity?.length > 0) setAmenityOpen(true);
  }, [filters.state, filters.designation, filters.activity, filters.amenity]);

  // Format filter label for display
  const formatFilterLabel = useCallback(
    (key, value) => {
      if (key === 'state') {
        // US_STATES has { label, value } format
        const state = US_STATES.find((s) => s.value === value);
        return state?.label || value;
      }
      if (key === 'activity') {
        // Find activity by slug
        const activity = activities.find((a) => slugify(a.name) === value);
        return activity?.name || value;
      }
      if (key === 'amenity') {
        // Find amenity by slug
        const amenity = amenities.find((a) => slugify(a.name) === value);
        return amenity?.name || value;
      }
      return value;
    },
    [activities, amenities]
  );

  // Build filter options with counts - show all states even if count is 0
  const stateOptions = useMemo(() => {
    const counts = {};
    parks.forEach((park) => {
      const parkStates = park.states?.split(',').map((s) => s.trim()) || [];
      parkStates.forEach((state) => {
        counts[state] = (counts[state] || 0) + 1;
      });
    });
    // Return ALL states - US_STATES has { label, value } format
    return US_STATES.map((state) => ({
      value: state.value,
      label: state.label,
      count: counts[state.value] || 0,
    }));
  }, [parks]);

  const designationOptions = useMemo(() => {
    const counts = {};
    parks.forEach((park) => {
      if (park.designation) {
        counts[park.designation] = (counts[park.designation] || 0) + 1;
      }
    });
    return designations
      .map((d) => ({
        value: d,
        label: d,
        count: counts[d] || 0,
      }))
      .filter((opt) => opt.count > 0)
      .sort((a, b) => b.count - a.count);
  }, [parks, designations]);

  // Build activity options grouped by category (like park detail page)
  const activityOptionsGrouped = useMemo(() => {
    // Count activities across all parks
    const counts = {};
    parks.forEach((park) => {
      park.activities?.forEach((name) => {
        if (name) {
          const slug = slugify(name);
          counts[slug] = (counts[slug] || 0) + 1;
        }
      });
    });

    // Group activities by category
    const grouped = {};
    activities.forEach((activity) => {
      const activityName = activity.name.toLowerCase();
      const slug = slugify(activity.name);
      const count = counts[slug] || 0;

      // Skip activities with 0 count
      if (count === 0) return;

      let categoryFound = false;
      for (const [category, config] of Object.entries(ACTIVITY_CATEGORIES)) {
        if (
          config.items.some(
            (item) => activityName.includes(item) || item.includes(activityName)
          )
        ) {
          if (!grouped[category]) {
            grouped[category] = {
              icon: config.icon,
              items: [],
            };
          }
          grouped[category].items.push({
            value: slug,
            label: activity.name,
            count,
          });
          categoryFound = true;
          break;
        }
      }

      // Add uncategorized activities to "Other" category
      if (!categoryFound) {
        if (!grouped['Other Activities']) {
          grouped['Other Activities'] = {
            icon: 'ellipsis',
            items: [],
          };
        }
        grouped['Other Activities'].items.push({
          value: slug,
          label: activity.name,
          count,
        });
      }
    });

    // Sort items within each category by count
    Object.values(grouped).forEach((category) => {
      category.items.sort((a, b) => b.count - a.count);
    });

    // Sort categories alphabetically, with "Other Activities" always at the bottom
    return Object.entries(grouped)
      .map(([name, config]) => ({
        name,
        icon: config.icon,
        items: config.items,
        totalCount: config.items.reduce((sum, item) => sum + item.count, 0),
      }))
      .sort((a, b) => {
        // "Other Activities" always goes to the bottom
        if (a.name === 'Other Activities') return 1;
        if (b.name === 'Other Activities') return -1;
        // Otherwise sort alphabetically
        return a.name.localeCompare(b.name);
      });
  }, [parks, activities]);

  // Flat list of all activity options for search filtering
  const activityOptions = useMemo(() => {
    return activityOptionsGrouped.flatMap((category) => category.items);
  }, [activityOptionsGrouped]);

  // Build amenity options grouped by category (like park detail page)
  const amenityOptionsGrouped = useMemo(() => {
    // Count amenities across all parks
    const counts = {};
    parks.forEach((park) => {
      park.amenities?.forEach((name) => {
        if (name) {
          const slug = slugify(name);
          counts[slug] = (counts[slug] || 0) + 1;
        }
      });
    });

    // Group amenities by category
    const grouped = {};
    amenities.forEach((amenity) => {
      const amenityName = amenity.name.toLowerCase();
      const slug = slugify(amenity.name);
      const count = counts[slug] || 0;

      // Skip amenities with 0 count
      if (count === 0) return;

      let categoryFound = false;
      for (const [category, config] of Object.entries(AMENITY_CATEGORIES)) {
        if (
          config.items.some(
            (item) => amenityName.includes(item) || item.includes(amenityName)
          )
        ) {
          if (!grouped[category]) {
            grouped[category] = {
              icon: config.icon,
              items: [],
            };
          }
          grouped[category].items.push({
            value: slug,
            label: amenity.name,
            count,
          });
          categoryFound = true;
          break;
        }
      }

      // Add uncategorized amenities to "Other" category
      if (!categoryFound) {
        if (!grouped['Other Amenities']) {
          grouped['Other Amenities'] = {
            icon: 'ellipsis',
            items: [],
          };
        }
        grouped['Other Amenities'].items.push({
          value: slug,
          label: amenity.name,
          count,
        });
      }
    });

    // Sort items within each category by count
    Object.values(grouped).forEach((category) => {
      category.items.sort((a, b) => b.count - a.count);
    });

    // Sort categories alphabetically, with "Other Amenities" always at the bottom
    return Object.entries(grouped)
      .map(([name, config]) => ({
        name,
        icon: config.icon,
        items: config.items,
        totalCount: config.items.reduce((sum, item) => sum + item.count, 0),
      }))
      .sort((a, b) => {
        // "Other Amenities" always goes to the bottom
        if (a.name === 'Other Amenities') return 1;
        if (b.name === 'Other Amenities') return -1;
        // Otherwise sort alphabetically
        return a.name.localeCompare(b.name);
      });
  }, [parks, amenities]);

  // Flat list of all amenity options for search filtering
  const amenityOptions = useMemo(() => {
    return amenityOptionsGrouped.flatMap((category) => category.items);
  }, [amenityOptionsGrouped]);

  // Create search-filtered options for the filter inputs
  const searchFilteredOptions = useMemo(() => {
    const filterBySearchTerm = (items, searchTerm) => {
      if (!searchTerm.trim()) return items;
      const term = searchTerm.toLowerCase().trim();
      return items.filter((item) => item.label.toLowerCase().includes(term));
    };

    // Filter groups - filter items within each category
    const filterGroups = (groups, searchTerm) => {
      if (!searchTerm.trim()) return groups;
      const term = searchTerm.toLowerCase().trim();
      return groups
        .map((group) => ({
          ...group,
          items: group.items.filter((item) =>
            item.label.toLowerCase().includes(term)
          ),
        }))
        .filter((group) => group.items.length > 0);
    };

    return {
      states: filterBySearchTerm(stateOptions, stateSearchTerm),
      designations: filterBySearchTerm(
        designationOptions,
        designationSearchTerm
      ),
      activities: filterBySearchTerm(activityOptions, activitySearchTerm),
      activityGroups: filterGroups(activityOptionsGrouped, activitySearchTerm),
      amenities: filterBySearchTerm(amenityOptions, amenitySearchTerm),
      amenityGroups: filterGroups(amenityOptionsGrouped, amenitySearchTerm),
    };
  }, [
    stateOptions,
    designationOptions,
    activityOptions,
    activityOptionsGrouped,
    amenityOptions,
    amenityOptionsGrouped,
    stateSearchTerm,
    designationSearchTerm,
    activitySearchTerm,
    amenitySearchTerm,
  ]);

  // Calculate valid filter options based on current filtered parks
  // Options with 0 matching parks (that aren't already selected) will be disabled
  const validFilterCounts = useMemo(() => {
    const stateCounts = {};
    const designationCounts = {};
    const activityCounts = {};
    const amenityCounts = {};

    filteredParks.forEach((park) => {
      // Count states
      const parkStates = park.states?.split(',').map((s) => s.trim()) || [];
      parkStates.forEach((state) => {
        stateCounts[state] = (stateCounts[state] || 0) + 1;
      });

      // Count designations
      if (park.designation) {
        designationCounts[park.designation] =
          (designationCounts[park.designation] || 0) + 1;
      }

      // Count activities
      park.activities?.forEach((name) => {
        if (name) {
          const slug = slugify(name);
          activityCounts[slug] = (activityCounts[slug] || 0) + 1;
        }
      });

      // Count amenities
      park.amenities?.forEach((name) => {
        if (name) {
          const slug = slugify(name);
          amenityCounts[slug] = (amenityCounts[slug] || 0) + 1;
        }
      });
    });

    return {
      states: stateCounts,
      designations: designationCounts,
      activities: activityCounts,
      amenities: amenityCounts,
    };
  }, [filteredParks]);

  // Helper to check if a filter option should be disabled
  const isOptionDisabled = useCallback(
    (filterType, value) => {
      // Don't disable if this option is already selected
      if (filters[filterType]?.includes(value)) return false;

      // Disable if no matching parks in current filtered results
      const counts =
        validFilterCounts[
          filterType === 'state'
            ? 'states'
            : filterType === 'designation'
              ? 'designations'
              : filterType === 'activity'
                ? 'activities'
                : 'amenities'
        ];
      return (counts[value] || 0) === 0;
    },
    [filters, validFilterCounts]
  );

  // Handle filter change from Checkbox component (events page style)
  const handleFilterChange = useCallback(
    (e, filterType) => {
      const filterValue = e.target.dataset.value || e.target.dataset.item;
      const isChecked = e.target.checked;

      if (isChecked) {
        addFilter(filterType, filterValue);
      } else {
        removeFilter(filterType, filterValue);
      }
    },
    [addFilter, removeFilter]
  );

  return (
    <>
      <Head>
        <title>Find a Park | ExploreNP</title>
        <meta
          name="description"
          content="Explore and discover national parks across America. Filter by state, designation, and activities."
        />
      </Head>

      <Container className="py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-gelica text-3xl md:text-4xl font-bold text-brown-100 mb-2">
            Find a Park
          </h1>
          <p className="text-brown-200">
            Discover {totalParks} national parks across America
          </p>
        </div>

        {/* Main Layout - Events page style */}
        <div className="lg:grid lg:grid-cols-[300px,auto] lg:gap-8">
          {/* Sidebar Filters - Desktop */}
          <div className="hidden lg:block z-0 bg-white border border-beige-100/50 rounded">
            <div className="top-0 left-0">
              {/* FILTER & SORT Header */}
              <div className="flex uppercase text-sm font-black py-4 pl-4">
                <AdjustmentsHorizontalIcon width={18} className="mr-4 -mt-1" />{' '}
                Filter & Sort
              </div>

              {/* Active Filters Section */}
              {activeFilterCount > 0 && (
                <div className="border-y border-beige-100/50 px-4 py-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-brown-100">
                      Active Filters
                    </span>
                    <button
                      onClick={() => {
                        clearFilters();
                        setStateSearchTerm('');
                        setDesignationSearchTerm('');
                        setActivitySearchTerm('');
                        setAmenitySearchTerm('');
                      }}
                      className="inline-flex items-center px-3 py-1 pt-1.5 text-xxs font-bold text-brown-100 bg-gray hover:bg-black/10 border border-black/20 hover:border-black/30 rounded-full transition-all duration-200 ease-in-out"
                      title="Remove all active filters"
                    >
                      Reset All
                    </button>
                  </div>
                  <p className="text-xs text-brown-100/60 mt-1">
                    {activeFilterCount} active filters
                  </p>
                </div>
              )}

              {/* Search Parks Input */}
              <div className="border-b border-beige-100/50 px-4 py-3">
                <input
                  type="text"
                  placeholder="Search parks..."
                  value={searchInput}
                  onChange={handleSearch}
                  className="w-full px-3 py-2 text-sm border border-beige-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-brown-400"
                />
              </div>

              {/* Activities Filter */}
              <Disclosure as="div" className="border-y border-beige-100/50">
                <Disclosure.Button
                  className="flex justify-between items-center w-full tracking-wide text-xs py-4 px-4"
                  onClick={() => setActivityOpen(!activityOpen)}
                >
                  <span>Activities</span>
                  <div className="flex items-center gap-2">
                    {filters.activity.length > 0 && (
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => {
                          e.stopPropagation();
                          setFilters((prev) => ({ ...prev, activity: [] }));
                          setActivitySearchTerm('');
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.stopPropagation();
                            setFilters((prev) => ({ ...prev, activity: [] }));
                            setActivitySearchTerm('');
                          }
                        }}
                        className="inline-flex items-center px-3 py-1 pt-1.5 text-xxs font-bold text-brown-100 bg-gray hover:bg-black/10 border border-black/20 hover:border-black/30 rounded-full transition-all duration-200 ease-in-out cursor-pointer"
                        title="Remove all activity filters"
                      >
                        Reset
                      </span>
                    )}
                    <ChevronRightIcon
                      width={18}
                      className={`ml-4 pointer-events-none transition-transform ${activityOpen ? 'rotate-90' : ''}`}
                    />
                  </div>
                </Disclosure.Button>
                {activityOpen && (
                  <div className="pb-6 pt-3 px-5">
                    {/* Active filter pills */}
                    {filters.activity.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {filters.activity.map((value) => (
                          <PillBadge
                            key={`activity-pill-${value}`}
                            item={{
                              label: formatFilterLabel('activity', value),
                              value,
                            }}
                            onClick={() => removeFilter('activity', value)}
                          />
                        ))}
                      </div>
                    )}

                    {/* Search input */}
                    <div className="mb-4 relative">
                      <TypeaheadInput
                        filterMode={true}
                        onInputChange={setActivitySearchTerm}
                        value={activitySearchTerm}
                        isClearable={true}
                        placeholder="Search activities"
                        className="w-full"
                      />
                    </div>

                    {/* Grouped activities */}
                    <div className="max-h-[300px] overflow-scroll filter-scrollbar mt-4">
                      {searchFilteredOptions.activityGroups.map((category) => (
                        <div key={category.name} className="mb-4">
                          <h4 className="sticky top-0 bg-white text-xxs font-serif font-bold text-brown-100 uppercase tracking-wider pt-1 pb-3 z-[10] shadow-[0_2px_4px_-2px_rgba(0,0,0,0.1)] flex items-center gap-2">
                            <i
                              className={`fa fa-${category.icon} text-green-100 text-sm w-4`}
                              aria-hidden="true"
                            />
                            {category.name}
                          </h4>
                          <ul className="flex flex-col gap-3 mt-2">
                            {category.items.map((item) => (
                              <Checkbox
                                key={`activity-${item.value}`}
                                label={item.label}
                                value={item.value}
                                tag="li"
                                className="flex"
                                onChange={(e) =>
                                  handleFilterChange(e, 'activity')
                                }
                                filters={filters.activity}
                                filterType="activity"
                                disabled={isOptionDisabled(
                                  'activity',
                                  item.value
                                )}
                              />
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </Disclosure>

              {/* Amenities Filter */}
              <Disclosure as="div" className="border-b border-beige-100/50">
                <Disclosure.Button
                  className="flex justify-between items-center w-full tracking-wide text-xs py-4 px-4"
                  onClick={() => setAmenityOpen(!amenityOpen)}
                >
                  <span>Amenities</span>
                  <div className="flex items-center gap-2">
                    {filters.amenity.length > 0 && (
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => {
                          e.stopPropagation();
                          setFilters((prev) => ({ ...prev, amenity: [] }));
                          setAmenitySearchTerm('');
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.stopPropagation();
                            setFilters((prev) => ({ ...prev, amenity: [] }));
                            setAmenitySearchTerm('');
                          }
                        }}
                        className="inline-flex items-center px-3 py-1 pt-1.5 text-xxs font-bold text-brown-100 bg-gray hover:bg-black/10 border border-black/20 hover:border-black/30 rounded-full transition-all duration-200 ease-in-out cursor-pointer"
                        title="Remove all amenity filters"
                      >
                        Reset
                      </span>
                    )}
                    <ChevronRightIcon
                      width={18}
                      className={`ml-4 pointer-events-none transition-transform ${amenityOpen ? 'rotate-90' : ''}`}
                    />
                  </div>
                </Disclosure.Button>
                {amenityOpen && (
                  <div className="pb-6 pt-3 px-5">
                    {/* Active filter pills */}
                    {filters.amenity.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {filters.amenity.map((value) => (
                          <PillBadge
                            key={`amenity-pill-${value}`}
                            item={{
                              label: formatFilterLabel('amenity', value),
                              value,
                            }}
                            onClick={() => removeFilter('amenity', value)}
                          />
                        ))}
                      </div>
                    )}

                    {/* Search input */}
                    <div className="mb-4 relative">
                      <TypeaheadInput
                        filterMode={true}
                        onInputChange={setAmenitySearchTerm}
                        value={amenitySearchTerm}
                        isClearable={true}
                        placeholder="Search amenities"
                        className="w-full"
                      />
                    </div>

                    {/* Grouped amenities */}
                    <div className="max-h-[300px] overflow-scroll filter-scrollbar mt-4">
                      {searchFilteredOptions.amenityGroups.map((category) => (
                        <div key={category.name} className="mb-4">
                          <h4 className="sticky top-0 bg-white text-xxs font-serif font-bold text-brown-100 uppercase tracking-wider pt-1 pb-3 z-[10] shadow-[0_2px_4px_-2px_rgba(0,0,0,0.1)] flex items-center gap-2">
                            <i
                              className={`fa fa-${category.icon} text-green-100 text-sm w-4`}
                              aria-hidden="true"
                            />
                            {category.name}
                          </h4>
                          <ul className="flex flex-col gap-3 mt-2">
                            {category.items.map((item) => (
                              <Checkbox
                                key={`amenity-${item.value}`}
                                label={item.label}
                                value={item.value}
                                tag="li"
                                className="flex"
                                onChange={(e) =>
                                  handleFilterChange(e, 'amenity')
                                }
                                filters={filters.amenity}
                                filterType="amenity"
                                disabled={isOptionDisabled(
                                  'amenity',
                                  item.value
                                )}
                              />
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </Disclosure>

              {/* State Filter */}
              <Disclosure as="div" className="border-b border-beige-100/50">
                <Disclosure.Button
                  className="flex justify-between items-center w-full tracking-wide text-xs py-4 px-4"
                  onClick={() => setStateOpen(!stateOpen)}
                >
                  <span>State</span>
                  <div className="flex items-center gap-2">
                    {filters.state.length > 0 && (
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => {
                          e.stopPropagation();
                          setFilters((prev) => ({ ...prev, state: [] }));
                          setStateSearchTerm('');
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.stopPropagation();
                            setFilters((prev) => ({ ...prev, state: [] }));
                            setStateSearchTerm('');
                          }
                        }}
                        className="inline-flex items-center px-3 py-1 pt-1.5 text-xxs font-bold text-brown-100 bg-gray hover:bg-black/10 border border-black/20 hover:border-black/30 rounded-full transition-all duration-200 ease-in-out cursor-pointer"
                        title="Remove all state filters"
                      >
                        Reset
                      </span>
                    )}
                    <ChevronRightIcon
                      width={18}
                      className={`ml-4 pointer-events-none transition-transform ${stateOpen ? 'rotate-90' : ''}`}
                    />
                  </div>
                </Disclosure.Button>
                {stateOpen && (
                  <div className="pb-6 pt-3 px-5">
                    {/* Active filter pills */}
                    {filters.state.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {filters.state.map((value) => (
                          <PillBadge
                            key={`state-pill-${value}`}
                            item={{
                              label: formatFilterLabel('state', value),
                              value,
                            }}
                            onClick={() => removeFilter('state', value)}
                          />
                        ))}
                      </div>
                    )}

                    {/* Search input */}
                    <div className="mb-4 relative">
                      <TypeaheadInput
                        filterMode={true}
                        onInputChange={setStateSearchTerm}
                        value={stateSearchTerm}
                        isClearable={true}
                        placeholder="Search states"
                        className="w-full"
                      />
                    </div>

                    {/* Scrollable checkbox list */}
                    <div className="max-h-[300px] overflow-scroll filter-scrollbar">
                      <ul className="relative w-full flex flex-col gap-2">
                        {searchFilteredOptions.states.map((item) => (
                          <Checkbox
                            key={`state-${item.value}`}
                            label={item.label}
                            value={item.value}
                            tag="li"
                            className="flex"
                            onChange={(e) => handleFilterChange(e, 'state')}
                            filters={filters.state}
                            filterType="state"
                            disabled={isOptionDisabled('state', item.value)}
                          />
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </Disclosure>

              {/* Park Type Filter */}
              <Disclosure as="div" className="border-b border-beige-100/50">
                <Disclosure.Button
                  className="flex justify-between items-center w-full tracking-wide text-xs py-4 px-4"
                  onClick={() => setDesignationOpen(!designationOpen)}
                >
                  <span>Park Type</span>
                  <div className="flex items-center gap-2">
                    {filters.designation.length > 0 && (
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => {
                          e.stopPropagation();
                          setFilters((prev) => ({
                            ...prev,
                            designation: [],
                          }));
                          setDesignationSearchTerm('');
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.stopPropagation();
                            setFilters((prev) => ({
                              ...prev,
                              designation: [],
                            }));
                            setDesignationSearchTerm('');
                          }
                        }}
                        className="inline-flex items-center px-3 py-1 pt-1.5 text-xxs font-bold text-brown-100 bg-gray hover:bg-black/10 border border-black/20 hover:border-black/30 rounded-full transition-all duration-200 ease-in-out cursor-pointer"
                        title="Remove all park type filters"
                      >
                        Reset
                      </span>
                    )}
                    <ChevronRightIcon
                      width={18}
                      className={`ml-4 pointer-events-none transition-transform ${designationOpen ? 'rotate-90' : ''}`}
                    />
                  </div>
                </Disclosure.Button>
                {designationOpen && (
                  <div className="pb-6 pt-3 px-5">
                    {/* Active filter pills */}
                    {filters.designation.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {filters.designation.map((value) => (
                          <PillBadge
                            key={`designation-pill-${value}`}
                            item={{
                              label: value,
                              value,
                            }}
                            onClick={() => removeFilter('designation', value)}
                          />
                        ))}
                      </div>
                    )}

                    {/* Search input */}
                    <div className="mb-4 relative">
                      <TypeaheadInput
                        filterMode={true}
                        onInputChange={setDesignationSearchTerm}
                        value={designationSearchTerm}
                        isClearable={true}
                        placeholder="Search park types"
                        className="w-full"
                      />
                    </div>

                    {/* Scrollable checkbox list */}
                    <div className="max-h-[300px] overflow-scroll filter-scrollbar">
                      <ul className="relative w-full flex flex-col gap-2">
                        {searchFilteredOptions.designations.map((item) => (
                          <Checkbox
                            key={`designation-${item.value}`}
                            label={item.label}
                            value={item.value}
                            tag="li"
                            className="flex"
                            onChange={(e) =>
                              handleFilterChange(e, 'designation')
                            }
                            filters={filters.designation}
                            filterType="designation"
                            disabled={isOptionDisabled(
                              'designation',
                              item.value
                            )}
                          />
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </Disclosure>
            </div>
          </div>

          {/* Main Content */}
          <div className="mt-5 lg:mt-0">
            {/* Mobile Filter Button */}
            <div className="lg:hidden mb-4">
              <button
                onClick={() => setShowMobileFilters(true)}
                className="flex items-center gap-1 px-3 py-1.5 text-sm text-brown-100 bg-beige-50 rounded-lg hover:bg-beige-100 transition-colors"
              >
                <AdjustmentsHorizontalIcon className="h-4 w-4" />
                Filters
                {activeFilterCount > 0 && (
                  <span className="bg-green-100 text-white text-xs px-1.5 rounded-full">
                    {activeFilterCount}
                  </span>
                )}
              </button>
            </div>

            {/* Map - no ClientOnly wrapper needed since Map is dynamically imported with ssr: false */}
            {showMap && (
              <div className="mb-4">
                <div className="h-[400px] rounded overflow-hidden border border-brown-100/10">
                  <Map
                    bounds={mapBounds}
                    style={{ height: '100%', borderRadius: '4px' }}
                  >
                    {/* State boundary outline when filtering by state */}
                    {filters.state?.length > 0 && (
                      <StateBoundary selectedStates={filters.state} />
                    )}
                    {parksByLocation.map(({ lat, lng, parks, locationKey }) => (
                      <ParkMarker
                        key={locationKey}
                        lat={lat}
                        lng={lng}
                        parks={parks}
                        isSelected={parks.some((p) => p.id === selectedParkId)}
                        onClick={handleParkSelect}
                      />
                    ))}
                  </Map>
                </div>
              </div>
            )}

            {/* Count and Grid/List Toggle - BELOW MAP */}
            <div className="flex justify-between items-center my-4">
              <p>
                <strong>{filteredParks.length}</strong>{' '}
                {filteredParks.length === 1 ? 'park' : 'parks'}
              </p>
              <div className="flex items-center gap-2">
                {/* Map Toggle */}
                <button
                  onClick={() => setShowMap(!showMap)}
                  className={clsx(
                    'flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg transition-colors',
                    showMap
                      ? 'bg-green-100 text-white'
                      : 'text-brown-100 bg-beige-50 hover:bg-beige-100'
                  )}
                >
                  <MapIcon className="h-4 w-4" />
                  Map
                </button>
                {/* Grid/List Toggle - hidden on mobile */}
                <div className="hidden md:flex items-center gap-1">
                  <button
                    onClick={() => setIsListView(true)}
                    className={clsx(
                      'p-1.5 rounded-lg transition-colors',
                      isListView
                        ? 'bg-beige-100 text-brown-100'
                        : 'text-brown-300 hover:bg-beige-50'
                    )}
                    title="List view"
                  >
                    <Bars3Icon className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => setIsListView(false)}
                    className={clsx(
                      'p-1.5 rounded-lg transition-colors',
                      !isListView
                        ? 'bg-beige-100 text-brown-100'
                        : 'text-brown-300 hover:bg-beige-50'
                    )}
                    title="Grid view"
                  >
                    <Squares2X2Icon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>

            {/* Active Filter Pills */}
            {activeFilterCount > 0 && (
              <nav className="mb-4">
                <ul className="relative w-full flex flex-wrap gap-2">
                  {activeFiltersArray.map((filter, index) => (
                    <PillBadge
                      key={`${filter.key}-${index}-${filter.value}`}
                      item={{
                        label: formatFilterLabel(filter.key, filter.value),
                        value: filter.value,
                      }}
                      onClick={() => removeFilter(filter.key, filter.value)}
                    />
                  ))}
                </ul>
              </nav>
            )}

            {/* Parks Grid */}
            {paginatedParks.length > 0 ? (
              <>
                <ParkItemsGrid
                  items={paginatedParks}
                  gridClassNames={`grid gap-6 my-5 ${isListView ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'}`}
                  isListView={isListView}
                />

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="mt-8">
                    <Pagination
                      totalCount={filteredParks.length}
                      pageSize={ITEMS_PER_PAGE}
                      currentPage={currentPage}
                      onPageChange={(page) => {
                        setCurrentPage(page);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                    />
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12 bg-white rounded-xl">
                <Squares2X2Icon className="h-12 w-12 text-beige-200 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-brown-100 mb-2">
                  No parks found
                </h3>
                <p className="text-brown-300 mb-4">
                  Try adjusting your filters to see more results
                </p>
                <button
                  onClick={clearFilters}
                  className="text-green-100 hover:text-green-200 font-semibold"
                >
                  Clear all filters
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Mobile Filters Modal */}
        <AnimatePresence>
          {showMobileFilters && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-[1000] lg:hidden"
              onClick={(e) => {
                if (e.target === e.currentTarget) {
                  setShowMobileFilters(false);
                }
              }}
            >
              <motion.div
                initial={{ x: '100%' }}
                animate={{ x: 0 }}
                exit={{ x: '100%' }}
                transition={{ type: 'spring', damping: 25 }}
                className="absolute right-0 top-0 bottom-0 w-80 max-w-full bg-white overflow-y-auto"
              >
                <div className="p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="font-gelica font-bold text-lg">Filters</h2>
                    <button
                      onClick={() => setShowMobileFilters(false)}
                      className="text-brown-300 hover:text-brown-100"
                    >
                      Close
                    </button>
                  </div>

                  {/* Search */}
                  <div className="mb-4">
                    <input
                      type="text"
                      placeholder="Search parks..."
                      value={searchInput}
                      onChange={handleSearch}
                      className="w-full px-3 py-2 text-sm border border-beige-200 rounded-lg"
                    />
                  </div>

                  {/* Activity Filter */}
                  <Disclosure as="div" className="border-y border-beige-100/50">
                    <Disclosure.Button
                      className="flex justify-between items-center w-full tracking-wide text-xs py-4 px-2"
                      onClick={() => setActivityOpen(!activityOpen)}
                    >
                      <span>Activities</span>
                      <div className="flex items-center gap-2">
                        {filters.activity.length > 0 && (
                          <span
                            role="button"
                            tabIndex={0}
                            onClick={(e) => {
                              e.stopPropagation();
                              setFilters((prev) => ({
                                ...prev,
                                activity: [],
                              }));
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.stopPropagation();
                                setFilters((prev) => ({
                                  ...prev,
                                  activity: [],
                                }));
                              }
                            }}
                            className="inline-flex items-center px-2 py-0.5 text-xxs font-bold text-brown-100 bg-gray hover:bg-black/10 border border-black/20 rounded-full cursor-pointer"
                          >
                            Reset
                          </span>
                        )}
                        <ChevronRightIcon
                          width={18}
                          className={`pointer-events-none transition-transform ${activityOpen ? 'rotate-90' : ''}`}
                        />
                      </div>
                    </Disclosure.Button>
                    {activityOpen && (
                      <div className="pb-4 px-2">
                        {/* Active filter pills */}
                        {filters.activity.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-3">
                            {filters.activity.map((value) => (
                              <PillBadge
                                key={`mobile-activity-pill-${value}`}
                                item={{
                                  label: formatFilterLabel('activity', value),
                                  value,
                                }}
                                onClick={() => removeFilter('activity', value)}
                              />
                            ))}
                          </div>
                        )}
                        <div className="max-h-[200px] overflow-scroll filter-scrollbar mt-4">
                          {activityOptionsGrouped.map((category) => (
                            <div
                              key={`mobile-${category.name}`}
                              className="mb-3"
                            >
                              <h4 className="sticky top-0 bg-white text-xxs font-serif font-bold text-brown-100 uppercase tracking-wider pt-1 pb-3 z-[10] shadow-[0_2px_4px_-2px_rgba(0,0,0,0.1)] flex items-center gap-2">
                                <i
                                  className={`fa fa-${category.icon} text-green-100 text-sm w-4`}
                                  aria-hidden="true"
                                />
                                {category.name}
                              </h4>
                              <ul className="flex flex-col gap-2 mt-2">
                                {category.items.map((item) => (
                                  <Checkbox
                                    key={`mobile-activity-${item.value}`}
                                    label={item.label}
                                    value={item.value}
                                    tag="li"
                                    className="flex"
                                    onChange={(e) =>
                                      handleFilterChange(e, 'activity')
                                    }
                                    filters={filters.activity}
                                    filterType="activity"
                                    disabled={isOptionDisabled(
                                      'activity',
                                      item.value
                                    )}
                                  />
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </Disclosure>

                  {/* Amenity Filter */}
                  <Disclosure as="div" className="border-b border-beige-100/50">
                    <Disclosure.Button
                      className="flex justify-between items-center w-full tracking-wide text-xs py-4 px-2"
                      onClick={() => setAmenityOpen(!amenityOpen)}
                    >
                      <span>Amenities</span>
                      <div className="flex items-center gap-2">
                        {filters.amenity.length > 0 && (
                          <span
                            role="button"
                            tabIndex={0}
                            onClick={(e) => {
                              e.stopPropagation();
                              setFilters((prev) => ({
                                ...prev,
                                amenity: [],
                              }));
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.stopPropagation();
                                setFilters((prev) => ({
                                  ...prev,
                                  amenity: [],
                                }));
                              }
                            }}
                            className="inline-flex items-center px-2 py-0.5 text-xxs font-bold text-brown-100 bg-gray hover:bg-black/10 border border-black/20 rounded-full cursor-pointer"
                          >
                            Reset
                          </span>
                        )}
                        <ChevronRightIcon
                          width={18}
                          className={`pointer-events-none transition-transform ${amenityOpen ? 'rotate-90' : ''}`}
                        />
                      </div>
                    </Disclosure.Button>
                    {amenityOpen && (
                      <div className="pb-4 px-2">
                        {/* Active filter pills */}
                        {filters.amenity.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-3">
                            {filters.amenity.map((value) => (
                              <PillBadge
                                key={`mobile-amenity-pill-${value}`}
                                item={{
                                  label: formatFilterLabel('amenity', value),
                                  value,
                                }}
                                onClick={() => removeFilter('amenity', value)}
                              />
                            ))}
                          </div>
                        )}
                        <div className="max-h-[200px] overflow-scroll filter-scrollbar mt-4">
                          {amenityOptionsGrouped.map((category) => (
                            <div
                              key={`mobile-${category.name}`}
                              className="mb-3"
                            >
                              <h4 className="sticky top-0 bg-white text-xxs font-serif font-bold text-brown-100 uppercase tracking-wider pt-1 pb-3 z-[10] shadow-[0_2px_4px_-2px_rgba(0,0,0,0.1)] flex items-center gap-2">
                                <i
                                  className={`fa fa-${category.icon} text-green-100 text-sm w-4`}
                                  aria-hidden="true"
                                />
                                {category.name}
                              </h4>
                              <ul className="flex flex-col gap-2 mt-2">
                                {category.items.map((item) => (
                                  <Checkbox
                                    key={`mobile-amenity-${item.value}`}
                                    label={item.label}
                                    value={item.value}
                                    tag="li"
                                    className="flex"
                                    onChange={(e) =>
                                      handleFilterChange(e, 'amenity')
                                    }
                                    filters={filters.amenity}
                                    filterType="amenity"
                                    disabled={isOptionDisabled(
                                      'amenity',
                                      item.value
                                    )}
                                  />
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </Disclosure>

                  {/* State Filter */}
                  <Disclosure as="div" className="border-b border-beige-100/50">
                    <Disclosure.Button
                      className="flex justify-between items-center w-full tracking-wide text-xs py-4 px-2"
                      onClick={() => setStateOpen(!stateOpen)}
                    >
                      <span>State</span>
                      <div className="flex items-center gap-2">
                        {filters.state.length > 0 && (
                          <span
                            role="button"
                            tabIndex={0}
                            onClick={(e) => {
                              e.stopPropagation();
                              setFilters((prev) => ({
                                ...prev,
                                state: [],
                              }));
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.stopPropagation();
                                setFilters((prev) => ({
                                  ...prev,
                                  state: [],
                                }));
                              }
                            }}
                            className="inline-flex items-center px-2 py-0.5 text-xxs font-bold text-brown-100 bg-gray hover:bg-black/10 border border-black/20 rounded-full cursor-pointer"
                          >
                            Reset
                          </span>
                        )}
                        <ChevronRightIcon
                          width={18}
                          className={`pointer-events-none transition-transform ${stateOpen ? 'rotate-90' : ''}`}
                        />
                      </div>
                    </Disclosure.Button>
                    {stateOpen && (
                      <div className="pb-4 px-2">
                        {/* Active filter pills */}
                        {filters.state.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-3">
                            {filters.state.map((value) => (
                              <PillBadge
                                key={`mobile-state-pill-${value}`}
                                item={{
                                  label: formatFilterLabel('state', value),
                                  value,
                                }}
                                onClick={() => removeFilter('state', value)}
                              />
                            ))}
                          </div>
                        )}
                        <div className="max-h-[200px] overflow-scroll filter-scrollbar">
                          <ul className="flex flex-col gap-3">
                            {stateOptions.map((item) => (
                              <Checkbox
                                key={`mobile-state-${item.value}`}
                                label={item.label}
                                value={item.value}
                                tag="li"
                                className="flex"
                                onChange={(e) => handleFilterChange(e, 'state')}
                                filters={filters.state}
                                filterType="state"
                                disabled={isOptionDisabled('state', item.value)}
                              />
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}
                  </Disclosure>

                  {/* Designation Filter */}
                  <Disclosure as="div" className="border-b border-beige-100/50">
                    <Disclosure.Button
                      className="flex justify-between items-center w-full tracking-wide text-xs py-4 px-2"
                      onClick={() => setDesignationOpen(!designationOpen)}
                    >
                      <span>Park Type</span>
                      <div className="flex items-center gap-2">
                        {filters.designation.length > 0 && (
                          <span
                            role="button"
                            tabIndex={0}
                            onClick={(e) => {
                              e.stopPropagation();
                              setFilters((prev) => ({
                                ...prev,
                                designation: [],
                              }));
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.stopPropagation();
                                setFilters((prev) => ({
                                  ...prev,
                                  designation: [],
                                }));
                              }
                            }}
                            className="inline-flex items-center px-2 py-0.5 text-xxs font-bold text-brown-100 bg-gray hover:bg-black/10 border border-black/20 rounded-full cursor-pointer"
                          >
                            Reset
                          </span>
                        )}
                        <ChevronRightIcon
                          width={18}
                          className={`pointer-events-none transition-transform ${designationOpen ? 'rotate-90' : ''}`}
                        />
                      </div>
                    </Disclosure.Button>
                    {designationOpen && (
                      <div className="pb-4 px-2">
                        {/* Active filter pills */}
                        {filters.designation.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-3">
                            {filters.designation.map((value) => (
                              <PillBadge
                                key={`mobile-designation-pill-${value}`}
                                item={{
                                  label: value,
                                  value,
                                }}
                                onClick={() =>
                                  removeFilter('designation', value)
                                }
                              />
                            ))}
                          </div>
                        )}
                        <div className="max-h-[200px] overflow-scroll filter-scrollbar">
                          <ul className="flex flex-col gap-3">
                            {designationOptions.map((item) => (
                              <Checkbox
                                key={`mobile-designation-${item.value}`}
                                label={item.label}
                                value={item.value}
                                tag="li"
                                className="flex"
                                onChange={(e) =>
                                  handleFilterChange(e, 'designation')
                                }
                                filters={filters.designation}
                                filterType="designation"
                                disabled={isOptionDisabled(
                                  'designation',
                                  item.value
                                )}
                              />
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}
                  </Disclosure>

                  {/* Apply Button */}
                  <Disclosure as="div" className="border-b border-beige-100/50">
                    <Disclosure.Button
                      className="flex justify-between items-center w-full tracking-wide text-xs py-4 px-2"
                      onClick={() => setAmenityOpen(!amenityOpen)}
                    >
                      <span>Amenities</span>
                      <div className="flex items-center gap-2">
                        {filters.amenity.length > 0 && (
                          <span
                            role="button"
                            tabIndex={0}
                            onClick={(e) => {
                              e.stopPropagation();
                              setFilters((prev) => ({
                                ...prev,
                                amenity: [],
                              }));
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.stopPropagation();
                                setFilters((prev) => ({
                                  ...prev,
                                  amenity: [],
                                }));
                              }
                            }}
                            className="inline-flex items-center px-2 py-0.5 text-xxs font-bold text-brown-100 bg-gray hover:bg-black/10 border border-black/20 rounded-full cursor-pointer"
                          >
                            Reset
                          </span>
                        )}
                        <ChevronRightIcon
                          width={18}
                          className={`pointer-events-none transition-transform ${amenityOpen ? 'rotate-90' : ''}`}
                        />
                      </div>
                    </Disclosure.Button>
                    {amenityOpen && (
                      <div className="pb-4 px-2">
                        {/* Active filter pills */}
                        {filters.amenity.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-3">
                            {filters.amenity.map((value) => (
                              <PillBadge
                                key={`mobile-amenity-pill-${value}`}
                                item={{
                                  label: formatFilterLabel('amenity', value),
                                  value,
                                }}
                                onClick={() => removeFilter('amenity', value)}
                              />
                            ))}
                          </div>
                        )}
                        <div className="max-h-[200px] overflow-scroll filter-scrollbar mt-4">
                          {amenityOptionsGrouped.map((category) => (
                            <div
                              key={`mobile-${category.name}`}
                              className="mb-3"
                            >
                              <h4 className="sticky top-0 bg-white text-xxs font-serif font-bold text-brown-100 uppercase tracking-wider pt-1 pb-3 z-[10] shadow-[0_2px_4px_-2px_rgba(0,0,0,0.1)] flex items-center gap-2">
                                <i
                                  className={`fa fa-${category.icon} text-green-100 text-sm w-4`}
                                  aria-hidden="true"
                                />
                                {category.name}
                              </h4>
                              <ul className="flex flex-col gap-2 mt-2">
                                {category.items.map((item) => (
                                  <Checkbox
                                    key={`mobile-amenity-${item.value}`}
                                    label={item.label}
                                    value={item.value}
                                    tag="li"
                                    className="flex"
                                    onChange={(e) =>
                                      handleFilterChange(e, 'amenity')
                                    }
                                    filters={filters.amenity}
                                    filterType="amenity"
                                    disabled={isOptionDisabled(
                                      'amenity',
                                      item.value
                                    )}
                                  />
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </Disclosure>

                  {/* Apply Button */}
                  <div className="mt-6">
                    <button
                      onClick={() => setShowMobileFilters(false)}
                      className="w-full py-3 bg-green-100 text-white font-semibold rounded-xl hover:bg-green-200 transition-colors"
                    >
                      Show {filteredParks.length} parks
                    </button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </Container>
    </>
  );
}

export async function getStaticProps() {
  // Get parks with minimal data for listing (lighter payload)
  const parks = await getParksForListing();

  // Get unique designations
  const designationsSet = new Set();
  parks.forEach((park) => {
    if (park.designation) {
      designationsSet.add(park.designation);
    }
  });
  const designations = Array.from(designationsSet).sort();

  // Get all activities
  const activitiesData = await prisma.activity.findMany({
    select: { id: true, name: true },
    orderBy: { name: 'asc' },
  });

  // Get all amenities
  const amenitiesData = await prisma.amenity.findMany({
    select: { id: true, name: true },
    orderBy: { name: 'asc' },
  });

  return {
    props: {
      parks: JSON.parse(JSON.stringify(parks)),
      designations,
      activities: activitiesData,
      amenities: amenitiesData,
      totalParks: parks.length,
    },
    revalidate: 86400, // Revalidate every 24 hours (data syncs weekly)
  };
}
